import React from "react";
import { LineChart, Ear, GraduationCap, FileText } from "lucide-react";

const POINTS = [
  {
    icon: LineChart,
    title: "Weekly KPI Tracking",
    desc: "Calls, appointments, and conversion rates — reviewed every week.",
  },
  {
    icon: Ear,
    title: "Call Monitoring & Quality Checks",
    desc: "Live and recorded calls reviewed for consistency and compliance.",
  },
  {
    icon: GraduationCap,
    title: "Ongoing Coaching",
    desc: "Continuous training so agents improve month over month.",
  },
  {
    icon: FileText,
    title: "Transparent Reporting",
    desc: "Clear dashboards and summaries — no black boxes, ever.",
  },
];

const Performance = () => {
  return (
    <section
      id="performance"
      data-testid="performance-section"
      style={{
        position: "relative",
        padding: "96px 0 144px",
        background: "rgba(10,10,10,0.70)",
        overflow: "hidden",
      }}
    >
      {/* Decorative background */}
      <div style={{ position: "absolute", inset: 0 }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "1px", background: "linear-gradient(90deg, transparent, rgba(210,170,60,0.15), transparent)" }} />
        <div
          style={{
            position: "absolute",
            top: "-160px",
            right: "0",
            width: "700px",
            height: "700px",
            borderRadius: "50%",
            filter: "blur(200px)",
            background: "radial-gradient(circle, rgba(210,170,60,0.06) 0%, transparent 70%)",
            animation: "hero-orb-2 18s ease-in-out infinite",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "15%",
            left: "-5%",
            width: "450px",
            height: "450px",
            borderRadius: "50%",
            filter: "blur(120px)",
            background: "radial-gradient(circle, rgba(243,201,69,0.04) 0%, transparent 70%)",
            animation: "hero-orb-3 20s ease-in-out infinite",
          }}
        />
      </div>

      <div style={{ position: "relative", maxWidth: "1280px", margin: "0 auto", padding: "0 24px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "64px" }}>
          {/* Left - text */}
          <div style={{ maxWidth: "560px", margin: "0 auto", textAlign: "center" }}>
            <div
              className="reveal"
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "10px",
                letterSpacing: "0.4em",
                color: "rgba(210,170,60,0.8)",
                textTransform: "uppercase",
                marginBottom: "20px",
              }}
            >
              Performance & Accountability
            </div>
            <h2
              data-testid="performance-headline"
              className="reveal reveal-delay-1"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "clamp(36px, 5vw, 60px)",
                fontWeight: 300,
                lineHeight: 1.08,
                color: "#fff",
                letterSpacing: "0.02em",
                marginBottom: "24px",
              }}
            >
              Managed Performance{" "}
              <em style={{ color: "#e8c45a", fontStyle: "italic", fontWeight: 400 }}>You Can Track</em>.
            </h2>
            <p
              className="reveal reveal-delay-2"
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "14px",
                fontWeight: 300,
                color: "rgba(220,195,140,0.6)",
                lineHeight: 1.8,
                letterSpacing: "0.02em",
                marginBottom: "32px",
              }}
            >
              We don't just provide agents — we ensure results through active management.
              Every engagement is measured, reviewed, and optimized.
            </p>

            {/* Live indicator */}
            <div
              className="reveal reveal-delay-3"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "12px",
                padding: "16px 24px",
                border: "1px solid rgba(210,170,60,0.2)",
                background: "rgba(210,170,60,0.04)",
              }}
            >
              <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#e8c45a", animation: "pulse 2s infinite" }} />
              <span
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: "12px",
                  fontWeight: 300,
                  color: "rgba(255,255,255,0.7)",
                  letterSpacing: "0.02em",
                }}
              >
                <span style={{ color: "#fff", fontWeight: 400 }}>Live dashboards.</span>{" "}
                Real-time visibility into your team's output.
              </span>
            </div>
          </div>
        </div>

        {/* Cards grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "20px", marginTop: "64px" }}>
          {POINTS.map((p, i) => (
            <div
              key={p.title}
              data-testid={`performance-card-${i}`}
              className="reveal"
              style={{
                padding: "32px 28px",
                background: "rgba(8,8,8,0.6)",
                border: "1px solid rgba(255,255,255,0.06)",
                transition: "all 0.35s ease",
                animationDelay: `${i * 90}ms`,
                cursor: "default",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "rgba(210,170,60,0.2)";
                e.currentTarget.style.background = "rgba(10,10,10,0.8)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)";
                e.currentTarget.style.background = "rgba(8,8,8,0.6)";
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  border: "1px solid rgba(255,255,255,0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "20px",
                }}
              >
                <p.icon style={{ width: "16px", height: "16px", color: "#e8c45a" }} />
              </div>
              <h3
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: "20px",
                  fontWeight: 400,
                  color: "#fff",
                  lineHeight: 1.25,
                  letterSpacing: "0.01em",
                  marginBottom: "10px",
                }}
              >
                {p.title}
              </h3>
              <p
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: "12px",
                  fontWeight: 300,
                  color: "rgba(255,255,255,0.5)",
                  lineHeight: 1.7,
                  letterSpacing: "0.01em",
                }}
              >
                {p.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Performance;
